import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import queryString from "query-string";

import { AnalyticsEvent } from "@vega-api/custom/analytics/analyticsEvents";
import { useAnalytics } from "@vega-api/custom/hooks/useAnalytics";
import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { setTxId } from "@vega-api/custom/stores/txId.store";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useLoginByAuthCode } from "@vega-api/generated/auth-login-v1/auth-login-v1";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { OauthProvider } from "@vega-api/generated/types/oauthProvider";
import { getProfile } from "@vega-api/generated/user-management-v1/user-management-v1";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { m } from "@vega-core/paraglide/messages";
import { LocalStorageService } from "@vega-core/service/LocalStorage.service";
import {
  getBaseURL,
  isGPPortal,
  isLocal,
  isUat,
} from "@vega-core/utils/global";
import { isTruthy } from "@vega-core/utils/isTruthy";
import {
  trackFirstLoginSucceeded,
  trackLoginFailed,
} from "@vega-core/utils/posthog/onboardingAnalytics";

import { CommonPaths } from "../../paths";
import { useOktaError } from "../../scenes/OktaAuthorise/useHandleError";

export const AuthCodeExchange = ({
  portalType,
}: {
  portalType: FrontendPortal;
}) => {
  const session = useSession();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const searchParams = new URLSearchParams(window.location.search);
  const authCode = searchParams.get("code");
  const redirect = searchParams.get("redirect");

  const { mutate } = useLoginByAuthCode({
    mutation: {
      onError: (error) => {
        console.error(error);
        trackLoginFailed({ error });

        const { description } = getErrorMessage(error, {
          defaultMessage: m.errors_unexpectedError(),
        });

        navigate("/login", { state: { error: description }, replace: true });
      },
      onSuccess: async (response) => {
        if (
          isTruthy(response.data?.accessToken) &&
          isTruthy(response.data.refreshToken)
        ) {
          LocalStorageService.setToken(response.data.accessToken);
          LocalStorageService.setRefreshToken(response.data.refreshToken);
          LocalStorageService.setOauthProvider(OauthProvider.OKTA);

          if (isTruthy(response.data.txId)) {
            setTxId(response.data.txId);
          }

          const { data } = await getProfile();

          if (data) {
            session.setUserData(data);

            trackEvent(AnalyticsEvent.AUTH_LOGIN_SUCCESS, {
              userId: data.id,
              userEligibilityType: data.extEligibility,
              userContactType: data.extContactType,
            });
            trackFirstLoginSucceeded({ userId: data.id });
          }
          navigate("/", { replace: true });
        }
      },
    },
    request: {
      skipInterceptors: true,
    },
  });

  useOktaError();

  const hasInitiatedExchange = useRef(false);

  useEffect(() => {
    const storedRedirect = sessionStorage.getItem("redirect");

    // Whole block below gets removed with dead code elimination on anything other than uat
    if ((redirect || storedRedirect) && isUat) {
      if (authCode && storedRedirect) {
        const url = new URL(storedRedirect);

        url.searchParams.append("code", authCode);

        sessionStorage.removeItem("redirect");

        window.location.assign(url.toString());
      } else if (redirect) {
        sessionStorage.setItem(
          "redirect",
          new URL(CommonPaths.OktaAuth, redirect).toString(),
        );

        const url = `${getBaseURL()}/api/authentication/v1/login/idp?${queryString.stringify(
          {
            source: isGPPortal
              ? FrontendPortal.GP_PORTAL
              : FrontendPortal.LP_PORTAL,
            oauthProvider: OauthProvider.OKTA,
            redirectToLocalhost: isLocal(),
          },
        )}`;
        window.location.assign(url.toString());
      }

      return;
    }

    if (authCode) {
      if (!hasInitiatedExchange.current) {
        hasInitiatedExchange.current = true;
        mutate({
          data: {
            authorizationCode: authCode,
            source: portalType,
            oauthProvider: OauthProvider.OKTA,
            redirectToLocalhost: isLocal(),
          },
        });
      }
    } else {
      navigate("/login", { replace: true });
    }
  }, [authCode, portalType, redirect, navigate, mutate]);

  return <LoadingSpinner />;
};
